<script lang="ts">
    // Svelte
    import { createEventDispatcher } from 'svelte';

    // Utils
    import { getCommerce } from '~/utils/commerce';
    import { getPlayback } from '~/utils/playback';
    import { isPremiumSubscriber } from '~/utils/user';

    // Shared Utils
    import { debounce } from '@amp/web-app-components/src/utils/debounce';
    import { loggerFor } from '@amp/web-apps-logger';

    // Stores
    import { fullScreenPlayerState } from '~/stores/full-screen-player';
    import { getI18n } from '~/stores/i18n';

    // Components
    import MetadataFavoriteButton from '~/components/structure/Players/components/MetadataFavoriteButton.svelte';
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';
    import HeaderAuthContent from '~/components/structure/header/HeaderAuthContent.svelte';
    import RightDrawer from '~/components/structure/RightDrawer.svelte';

    // Shared Components
    import PlayerLCD from '@amp/web-app-components/src/components/PlayerLCD/PlayerLCD.svelte';
    import PlaybackControls from '@amp/web-app-components/src/components/PlaybackControls/PlaybackControls.svelte';

    // Types
    import type { QueueItemMetadata } from '~/jet/models';

    export let item: QueueItemMetadata | null = null;
    export let playbackType: number | null = null;

    const CHROME_PLAYER_BREAKPOINT = 1000;
    const MINI_PLAYER_BREAKPOINT = 483;

    const dispatch = createEventDispatcher();
    const i18n = getI18n();
    const log = loggerFor('<ChromePlayer>');
    const { queue } = getPlayback();
    const { state: commerceState } = getCommerce();

    // Property to indicate where to render <amp-contextual-menu />
    // otherwise it defaults to document.body
    let parentElement: HTMLElement = null;

    let playerElement: any;
    let isExpanded: boolean = false;
    let isExpandable: boolean = false;

    function dropOnLCD({ detail: { data } }) {
        // TODO: rdar://139877441 ([Classical on Web]: Player: Support Drag and Drop)
    }

    function setIsExpandable(_e: Event) {
        isExpandable =
            typeof window !== 'undefined' &&
            window?.innerWidth < CHROME_PLAYER_BREAKPOINT &&
            window.innerWidth > MINI_PLAYER_BREAKPOINT;
    }

    function toggleExpanded(e): void {
        if (isExpandable && !!item && e.target.type !== 'button') {
            isExpanded = !isExpanded;
        }
    }

    $: isPremiumUser = isPremiumSubscriber(
        $commerceState?.user?.subscriberType,
    );

    $: isBuffering = $queue.derivedStates.isLoading;

    $: toggleLabel = (() => {
        let label;
        if (isExpandable) {
            if (isExpanded) {
                label = $i18n.t('ASE.Web.MusicClassical.Controls.Collapse');
            } else {
                label = $i18n.t('ASE.Web.MusicClassical.Controls.Expand');
            }
        }
        return label;
    })();

    const handleKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Tab') {
            dispatch('playerTabKeyDown');
        }
    };
</script>

<svelte:window
    on:load={debounce(setIsExpandable, 200)}
    on:resize={debounce(setIsExpandable, 200)}
/>
<!--
    Hide the WCs on SSR. The cause a flash of unstyled slots before the
    WC code loads. We could consider actually SSRing these to avoid this
    problem, but that requires personalization, so best to hold off:
    rdar://97613131 (Serverside render MK WCs)
-->
{#if typeof window !== 'undefined'}
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class="chrome-player"
        class:chrome-player__expanded={isExpanded}
        class:chrome-player__initial={!item}
        data-testid="chrome-player"
        on:keydown={handleKeydown}
        bind:this={parentElement}
    >
        <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
        <div
            class="chrome-player__container"
            on:click={toggleExpanded}
            role={isExpandable ? 'button' : undefined}
            aria-pressed={isExpanded}
            tabindex={isExpandable ? 0 : -1}
            aria-label={toggleLabel}
            data-testid="chrome-player-container"
        >
            <div class="chrome-player__playback-controls">
                <PlaybackControls realm="classical" />
            </div>

            <div class="chrome-player__lcd">
                <PlayerLCD
                    realm="classical"
                    dropEnabled={true}
                    hasBadge={isPremiumUser}
                    {item}
                    {playbackType}
                    {isBuffering}
                    translateFn={$i18n.t}
                    on:dropOnLCD={dropOnLCD}
                    on:launchFullScreenPlayer
                    on:navigate
                >
                    <div
                        class="right-content-compact"
                        slot="right-content-compact"
                    >
                        <HeaderAuthContent />
                    </div>
                    <div class="lcd-action-right" slot="lcd-action-right">
                        {#if isPremiumUser && item}
                            <MetadataFavoriteButton
                                id={item.id}
                                platter={false}
                            />
                        {/if}
                    </div>
                    <div class="context-menu" slot="context-menu">
                        {#if item?.contextMenuAction}
                            <ProductContextMenu
                                contextMenuAction={item.contextMenuAction}
                                buttonStyle="non-platter"
                                bind:parentElement
                            />
                        {/if}
                    </div>
                </PlayerLCD>
            </div>

            <div class="chrome-player__meta-controls">
                <div class="chrome-player__volume">
                    <amp-chrome-volume />
                </div>
                <div class="right-content">
                    <div class="action-buttons">
                        <!-- rdar://140938579 ([Classical on Web] - Info - Sidebar) -->
                        <!--<RightDrawer
                            showFullScreenPlayer={$fullScreenPlayerState.isVisible}
                            on:launchFullScreenPlayer
                        />-->
                        <RightDrawer on:launchFullScreenPlayer />
                    </div>
                    <HeaderAuthContent />
                </div>
            </div>
            <div class="chrome-player__expanded-panel">
                <div
                    class="chrome-player__compact-controls-base chrome-player__compact-controls-base--expanded"
                >
                    <PlaybackControls realm="classical" />
                </div>
            </div>
        </div>
    </div>
{/if}

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/viewports' as *;
    @use 'ac-sasskit/modules/viewportcontent/core' as *;
    @use '../../../../styles/music-classical/core/globalvars' as *;

    .chrome-player {
        --chrome-player-padding: 5px;
        --ctxmenu-trigger-opacity: 0;
        --playerInnerShadow: var(--chromePlayerInnerShadow);
        --playerDropShadow2: var(--chromePlayerDropShadow);
        --playerBoxShadowInset: #{$player-box-shadow-inset-light};
        --thumb-opacity: 0;
        --lcd-border-radius: 2px;
        margin: 0 auto;
        padding: var(--chrome-player-padding);
        width: 100%;
        height: 54px;
        display: block;
        box-sizing: border-box;
        position: relative;
        color: var(--systemSecondary);
        box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05),
            0 1px 3px var(--playerDropShadow2),
            inset var(--playerBoxShadowInset) var(--playerInnerShadow);

        @media (--target-touchscreen) {
            --thumb-opacity: 1;
        }

        @media (--range-xsmall-down) {
            --lcd-border-width: 0;
        }

        @media (prefers-color-scheme: dark) {
            --playerBoxShadowInset: #{$player-box-shadow-inset-dark};
            background-image: none;
        }
    }

    .chrome-player::before {
        --playerGradientTop: #{$player-gradient-top};
        --playerGradientBottom: #{$player-gradient-bottom};
        content: '';
        width: 100%;
        height: 100%;
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        background-color: var(--chromePlayerBGFill);
        background-image: linear-gradient(
            to bottom,
            var(--playerGradientTop) 0,
            var(--playerGradientBottom) 100%
        );
        backdrop-filter: saturate(50%) blur(20px);

        @media (prefers-color-scheme: dark) {
            background-image: none;
        }
    }

    .chrome-player:hover {
        // define these on hover so that declarations referring to them
        // don't use fallbacks
        --ctxmenu-trigger-opacity: 1;
        --thumb-opacity: 1;
        --timestamp-hover: 1;
        --lcd-meta-badge-placeholder-width: 24px;

        @media (--range-xsmall-down) {
            --lcd-secondary-width: 100%;
        }

        // Hide track duration on hover of secondary marquee line or if paddle controls have focus
        // when marquee paddle controls are available and marquee scrolling is enabled
        &:global(
                :has(.marquee--secondary:hover .active):has(
                        .paddle-controls-container
                    )
            ),
        &:global(
                :has(.marquee--secondary .active):has(
                        .paddle-controls-container:focus-within
                    )
            ) {
            --timestamp-hover: 0;
        }
    }

    .chrome-player__expanded {
        @media (--range-xsmall-down) {
            --lcd-height: 54px;
            --chrome-player-padding: 0;
            height: 118px;
            padding: 0;
        }
    }

    .chrome-player__container {
        margin: 0 auto;
        width: 100%;
        max-width: viewport-content-for(xlarge);
        height: var(--lcd-height, 44px);
        display: grid;
        grid-template-columns:
            $playback-controls-grid-column-width minmax(
                $lcd-width,
                $lcd-max-width
            )
            $meta-controls-grid-column-width; // Matched to spec
        grid-template-areas: 'playback lcd controls';
        align-items: center;
        position: relative;
        color: var(--systemSecondary);

        @media (--range-xsmall-down) {
            display: flex;

            .chrome-player.chrome-player__expanded & {
                @media (--range-xsmall-down) {
                    margin: 0;
                    width: 100%;
                    height: 118px;
                    display: grid;
                    grid-template:
                        'lcd' 54px
                        'expanded' 64px / 1fr;
                }
            }
        }

        :global(.playback-controls) {
            @media (prefers-color-scheme: dark) {
                color-scheme: dark;
            }
        }
    }

    .chrome-player:not(.chrome-player__initial) .chrome-player__container {
        --keyColor-focus-color: rgba(var(--keyColor-rgb), 0.6);
        --sk-focus-color: rgba(var(--keyColor-rgb), 0.6);
        --sk-focus-offset: 0;

        @media (--range-xsmall-down) {
            @supports #{'selector(:has(:focus-visible))'} {
                &:focus {
                    outline: none;
                }
            }
        }
    }

    .chrome-player__playback-controls {
        --skip-control-color: var(--systemSecondary);
        margin: 0 auto;
        padding: 0 16px;
        width: auto;
        grid-area: playback;

        @media (--range-xsmall-down) {
            display: none;
        }
    }

    .chrome-player__playback-controls--compact {
        padding: 0 10px;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--playerLCDBGFill);

        .chrome-player.chrome-player__initial
            &
            .chrome-player__compact-controls-base {
            @media (--range-xsmall-down) {
                display: none;
            }
        }

        @media (--small) {
            display: none;
        }
    }

    .chrome-player__compact-controls-base {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        direction: ltr;
    }

    .chrome-player__lcd {
        width: 100%;
        grid-area: lcd;
        align-self: start;
        overflow: hidden;

        .right-content-compact {
            // Add margin between player buttons and user auth content
            margin-inline-start: 10px;
        }

        @media (--range-xsmall-down) {
            position: relative;
        }

        &::before {
            @media (--range-xsmall-down) {
                // draw border around player for compact view
                content: '';
                position: absolute;
                inset: 0;
                border-radius: 2px;
                box-shadow: inset 0 0 0 0.5px $lcd-border-color;
                z-index: var(--z-default);
                pointer-events: none;
            }

            @media (--small) {
                display: none;
            }
        }
    }

    .chrome-player__meta-controls {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-template-rows: 1fr;
        grid-template-areas: 'volume app';
        grid-area: controls;

        // `right-content` slot technically flips based on ltr / rtl,
        // but is coming down as `right-content` slot from amp web components.
        .right-content {
            display: flex;
            gap: 14px;
        }

        @media (--small) {
            margin-inline-end: 15px;
        }

        @media (--range-xsmall-down) {
            display: none;
        }
    }

    .chrome-player__volume {
        --chromeVolumeElapsed: var(--chromePlayerTrackElapsedColor);
        --chromeVolumeTrack: var(--chromePlayerVolumeTrackColor);
        --chromeVolumeThumb: var(--chromePlayerVolumeThumbColor);
        --chromeVolumeThumbShadowInset: var(--systemSecondary-onLight);
        --chromeVolumeThumbShadow: var(--systemQuaternary);
        --chromeVolumeIcon: var(--chromePlayerVolumeIconColor);
        margin: auto;
        width: auto;
        grid-area: volume;

        @media (--range-xsmall-down) {
            display: none;
        }
    }

    .chrome-player__expanded-panel {
        display: none;
        grid-area: expanded;

        .chrome-player.chrome-player__expanded & {
            @media (--range-xsmall-down) {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        }
    }

    .action-buttons {
        display: flex;
        gap: 2px;
    }

    .context-menu {
        // Height of "menu-slot-wrapper" in the LCD component
        $menuSlotHeight: 16px;

        // Context menu button size passed to ProductContextMenu component
        --contextMenuButtonSize: 30px;

        // Nudge button upward to center it in the given space
        margin-top: calc(
            -1 * (var(--contextMenuButtonSize) - #{$menuSlotHeight}) / 2
        );

        // Nudge "..." closer to text, since we do not see the platter
        margin-inline-start: calc(-1 * var(--contextMenuButtonSize) / 4);
    }

    .lcd-action-right {
        // Favorite button overrides
        --favoriteButtonSize: 16px;
        --favoriteIconSize: 10px;
        --favoriteButtonStarOutline: var(--systemSecondary);
        --favoriteButtonStarOutline-hover: var(--systemSecondary);
        --favoriteButtonStarFill: var(--systemSecondary);
        border-radius: $global-border-radius-xsmall;

        &:hover {
            background: var(--systemQuaternary);
        }
    }
</style>
